import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getValues } from 'api/common';
import { RootState } from '..';
import * as defaultData from '../../utilities/data';
import { TEventStatus } from 'scenes/Calendar/CalendarScreen';
import { TVisibilityOption } from 'scenes/CreateEvent/CreateEvent';

export type TWidgetState = {
  isWidget: boolean;
  inviter: string;
};

export type TGroupState = 'allow_all_with_notification' | 'allow_with_release' | 'forbidden';

interface IRestriction {
  overall: string;
  profile: string;
  groupchat: string;
  groupchatWilkar: string;
  comments: string;
  commentsWilkar: string;
  likes: string;
  post: string;
}

export const getAppValues = createAsyncThunk('common/getAppValues', async () => {
  const res = await getValues();
  return res;
});

const initialState = {
  loading: false,
  maximal_membership_count: 9999,
  sign_up_description: '',
  sign_up_description_without_code: '',
  sign_up_description_with_code: '',
  sign_up_description_guests: '',
  show_all_mentors: false,
  data: defaultData,
  notifications_config: {} as { own_post_notify: boolean | undefined },
  disable_code_registration: false,
  disable_guest_function: true,
  widget: {} as TWidgetState,
  social_media: false,
  allow_doc_deletion: true,
  guest_naming_singular: 'Gast',
  guest_naming_plural: 'Gäste',
  member_naming_singular: 'Mitglied',
  tou_link: 'https://www.connact.app/agb',
  dpd_link: 'https://www.connact.app/datenschutz',
  accept_agb_at_passwort_reset: false,
  users_can_create_new_corps: false,
  users_can_request_new_corps: false,
  allow_bio: false,
  allow_link: false,
  allow_attachments: false,
  newsfeed_popup: false,
  event_show_website_option: false,
  event_default_visibility_option: 'all' as TVisibilityOption,
  event_default_tab: 'all' as TEventStatus,
  show_noninvited_events: true,
  event_standard_link_registration: 'App', // for now unused, delete comment when im needed
  event_standard_registration_option_link: 'app',
  event_creation_ask_registration_type: false,
  restrictions: {} as IRestriction,
  allow_mentoring_edit: false,
  support_email: 'support@connact.app',
  support_phone: '+49 170 49 805 94',
  support_phone_number: '',
  support_name: '',
  event_participants_limitation: 0, // if == 0, this feature is disabled
  html_preview_height: 10,
  registration_agb_checkbox: false,
  first_login_accept_agb: null,
  admin_can_delete_posts: false,
  map_ask_user_to_hide: false,
  info_section_data: '',
  show_phone: false,
  show_member_id_title: false,
  member_id_title: '',
  check_member_id: false,
  impressum: 'https://connact.app/impressum',
  map_see_users_detailed: false,
  events_parts_possible: true,
  events_options_possible: true,
  log_in_description: '',
  disable_registration_without_code: false,
  activate_guests_automatically: false,
  subscription_feature: false,
  function_read_only: false,
  user_can_remove_corps: true,
  show_unsubscribe_magazine: false,
  create_public_group_chats: 'forbidden' as TGroupState,
  default_event_view_list: false,
  only_gods_change_document_root: false,
};

export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setIsWidget: (state, action: PayloadAction<TWidgetState>) => {
      state.widget = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAppValues.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAppValues.fulfilled, (state, action: any) => {
      state.loading = false;
      const maximal_membership_count = !Number(action.payload?.maximal_membership_count)
        ? 9999
        : action.payload.maximal_membership_count;
      state.maximal_membership_count = maximal_membership_count;
      state.sign_up_description = action.payload?.sign_up_description;
      state.sign_up_description_without_code = action.payload?.sign_up_description_without_code;
      state.sign_up_description_with_code = action.payload?.sign_up_description_with_code;
      state.sign_up_description_guests = action.payload?.sign_up_description_guests;

      if (action.payload?.charge) state.data.corpsFunction = action.payload.charge;
      if (action.payload?.status) state.data.corpsStatuses = action.payload.status;

      state.show_all_mentors = action.payload.show_all_mentors ?? false;

      if (action.payload?.notificationsConfig?.ownPostNotify)
        state.notifications_config.own_post_notify =
          action.payload?.notificationsConfig?.ownPostNotify === '1' ? true : false;

      state.disable_code_registration = action.payload?.disable_code_registration ?? false;

      state.disable_guest_function = action.payload?.disable_guest_function ?? true;

      state.social_media = !!action.payload?.social_media;
      state.allow_doc_deletion = !action.payload?.allow_doc_deletion ? action.payload?.allow_doc_deletion : true;

      if (action.payload?.guest_naming_singular) state.guest_naming_singular = action.payload.guest_naming_singular;
      if (action.payload?.guest_naming_plural) state.guest_naming_plural = action.payload.guest_naming_plural;
      if (action.payload?.member_naming_singular) state.member_naming_singular = action.payload.member_naming_singular;

      if (action.payload?.dpd_link) state.dpd_link = action.payload.dpd_link;
      if (action.payload?.tou_link) state.tou_link = action.payload.tou_link;

      state.accept_agb_at_passwort_reset = action.payload?.accept_agb_at_passwort_reset ?? false;

      state.users_can_create_new_corps = action.payload?.users_can_create_new_corps ?? false;
      state.users_can_request_new_corps = action.payload?.users_can_request_new_corps ?? false;

      state.allow_bio = action.payload?.allow_bio ?? false;
      state.allow_link = action.payload?.allow_link ?? false;
      state.allow_attachments = action.payload?.allow_attachments ?? false;
      state.newsfeed_popup = action.payload?.newsfeed_popup ?? false;

      state.show_noninvited_events = action.payload?.show_noninvited_events ?? true;

      state.event_show_website_option = action.payload?.event_show_website_option
        ? action.payload?.event_show_website_option
        : false;

      state.event_default_visibility_option = action.payload?.event_default_visibility_option
        ? action.payload?.event_default_visibility_option.toLowerCase()
        : 'all';

      state.event_default_tab = action.payload?.event_default_tab
        ? action.payload?.event_default_tab.toLowerCase()
        : 'all';

      state.event_creation_ask_registration_type = action.payload?.event_creation_ask_registration_type ?? false;

      state.registration_agb_checkbox = action.payload?.registration_agb_checkbox ?? false;

      state.first_login_accept_agb = action.payload?.first_login_accept_agb ?? false;

      state.allow_mentoring_edit = action.payload?.allow_mentoring_edit ?? false;
      state.support_email = action.payload?.support_email ?? 'support@connact.app';
      state.support_name = action.payload?.support_name ?? '';
      state.support_phone_number = action.payload?.support_phone_number ?? '+49 170 49 805 94';

      state.event_standard_registration_option_link = action.payload?.event_standard_registration_option_link
        ? action.payload?.event_standard_registration_option_link
        : 'app';

      state.event_standard_link_registration = action.payload?.event_standard_link_registration
        ? action.payload?.event_standard_link_registration
        : '';

      state.restrictions = action.payload?.restrictions ? action.payload?.restrictions : state.restrictions;

      state.event_participants_limitation = +action.payload?.event_participants_limitation || 0;

      state.html_preview_height = +action.payload?.html_preview_height || 10;

      state.show_member_id_title = action.payload?.show_member_id_title
        ? action.payload.show_member_id_title
        : state.show_member_id_title;

      state.check_member_id = action.payload?.check_member_id ? action.payload.check_member_id : state.check_member_id;

      state.member_id_title = action.payload?.member_id_title ? action.payload.member_id_title : state.member_id_title;
      state.info_section_data = action.payload?.info_section_data ? action.payload?.info_section_data : '';

      state.impressum = action.payload.impressum ? action.payload.impressum : state.impressum;

      state.admin_can_delete_posts = action.payload?.admin_can_delete_posts ?? false;

      state.map_ask_user_to_hide = action.payload?.map_ask_user_to_hide ?? false;

      state.show_phone = action.payload?.show_phone ?? false;

      state.map_see_users_detailed = action.payload?.map_see_users_detailed ?? false;

      state.events_parts_possible = action.payload?.events_parts_possible ?? true;
      state.events_options_possible = action.payload?.events_options_possible ?? true;

      state.log_in_description = action.payload?.log_in_description;

      state.disable_registration_without_code = action.payload?.disable_registration_without_code ?? false;
      state.activate_guests_automatically = action.payload?.activate_guests_automatically ?? false;

      state.subscription_feature = action.payload?.subscription_feature ?? false;
      state.function_read_only = action.payload?.function_read_only ?? false;
      state.user_can_remove_corps = action.payload?.user_can_remove_corps ?? true;
      state.show_unsubscribe_magazine = action.payload?.show_unsubscribe_magazine ?? false;
      state.create_public_group_chats = action.payload?.create_public_group_chats || 'forbidden';
      state.default_event_view_list = action.payload?.default_event_view_list ?? false;
      state.only_gods_change_document_root = action.payload?.only_gods_change_document_root ?? false;
    });
    builder.addCase(getAppValues.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const selectCommonState = (state: RootState) => state.common;

export const commonActions = commonSlice.actions;

export const common = commonSlice.reducer;
